/**
 * 1: Setting no validate or validationSchema clears out imperative error state on onBlur/onChange
 * https://github.com/jaredpalmer/formik/issues/834
 *
 * We abuse 'status' in this case
 */
import React from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useFormikContext } from 'formik';
import { Button, Col, Form } from 'react-bootstrap';
import { SelectField } from '@components/Form/SelectField';
import { ActivityIndicator } from '@components/ActivityIndicator/ActivityIndicator';
import { DEFAULT_COUNTRY_CODE } from '@constants/constants';
import { BusinessInformation } from './BusinessInformation';
import { BusinessInformationExtra } from './BusinessInformationExtra';
import { LoginData } from './LoginData';

interface IRegisterBusinessAccountProps {
  countryData: ICountry[];
}

export const RegisterBusinessAccount = ({ countryData, ...props }: IRegisterBusinessAccountProps) => {
  const { t } = useTranslation('register');
  const { handleSubmit, isSubmitting, status, errors, setErrors } = useFormikContext();

  const scrollToErrors = () => {
    setTimeout(() => {
      const element = document.querySelector('.form-control.is-invalid');
      // element.scrollInToView is very inconsistent
      element &&
        window.scroll({
          top: 0,
          left: 0,
          behavior: 'smooth'
        });
    }, 0);
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const blockingStatus = Object.fromEntries(
      Object.entries(status ?? {}).filter(([, value]) => value !== 'form-errors:unavailable.vat-number')
    );
    const formErrors = { ...blockingStatus, ...errors };
    const hasErrors = Object.keys(formErrors).length > 0;

    if (hasErrors) {
      setErrors(formErrors);
      scrollToErrors();
    } else {
      handleSubmit();
    }
  };

  return (
    <>
      {isSubmitting && <ActivityIndicator blockUI />}
      <Form noValidate onSubmit={handleFormSubmit}>
        <div className="border border-bottom-0">
          <Form.Row>
            <Form.Group as={Col} xs={12} md={6}>
              <SelectField label={t('label.country')} name="countryCode" defaultValue={DEFAULT_COUNTRY_CODE}>
                <option value=""></option>
                {countryData.map((country) => (
                  <option key={country.code} value={country.code}>
                    {country.name}
                  </option>
                ))}
              </SelectField>
            </Form.Group>
          </Form.Row>
        </div>
        <div className="border border-bottom-0">
          <BusinessInformation countryData={countryData} />
        </div>
        <div className="border border-bottom-0">
          <BusinessInformationExtra {...props} />
        </div>
        <div className="border">
          <LoginData />
        </div>
        <div className="mt-3 text-right">
          {/* @ts-expect-error size="xl" */}
          <Button type="submit" size="xl" disabled={isSubmitting} data-testid="register-submit" onClick={scrollToErrors}>
            {t('button.create-account')}
          </Button>
        </div>
      </Form>
    </>
  );
};

export default RegisterBusinessAccount;
